import React from 'react';
import { cn } from "../../lib/utils";
import { Switch } from "../ui/switch";
import { Folder, Bell, BellOff, AlertCircle } from "lucide-react";
import { Badge } from "../ui/badge";

export default function FolderSidebar({
    folders,
    alertCounts,
    selectedFolder,
    onSelectFolder,
    onToggleFollow,
    isLoading
}) {
    return (
        <div className="h-full bg-slate-950 border-r border-slate-800 flex flex-col">
            <div className="p-6 border-b border-slate-800 flex flex-col items-center">
                <img
                    src="/L2TALERT.png"
                    alt="L2T AlertX"
                    className="h-20 w-auto object-contain drop-shadow-lg transform transition-transform hover:scale-105"
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                        // Fallback text inject via JS if image fails
                        const fallbackTitle = document.createElement('h2');
                        fallbackTitle.className = 'text-xl font-bold text-white mt-2';
                        fallbackTitle.innerText = 'L2T AlertX';
                        e.target.parentNode.appendChild(fallbackTitle);
                    }}
                />
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {isLoading ? (
                    <div className="space-y-2">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="h-16 bg-slate-800/50 rounded-xl animate-pulse" />
                        ))}
                    </div>
                ) : folders.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                        <AlertCircle className="w-10 h-10 mx-auto mb-3 opacity-50" />
                        <p className="text-sm font-medium">No signal systems available</p>
                        <p className="text-xs text-slate-600 mt-1">Contact admin to enable systems</p>
                    </div>
                ) : (
                    folders.map(folder => {
                        const isSelected = selectedFolder?.id === folder.id;
                        const isFollowing = folder.is_enabled === true;
                        const count = alertCounts[folder.id] || 0;

                        return (
                            <div
                                key={folder.id}
                                className={cn(
                                    "group relative rounded-xl p-4 cursor-pointer transition-all duration-200",
                                    isSelected
                                        ? "bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30"
                                        : "hover:bg-slate-800/60 border border-transparent"
                                )}
                                onClick={() => onSelectFolder(folder)}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3 min-w-0 flex-1">
                                        <div className={cn(
                                            "w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-colors",
                                            isFollowing ? "bg-blue-500/20" : "bg-slate-800"
                                        )}>
                                            <Folder className={cn(
                                                "w-5 h-5",
                                                isFollowing ? "text-blue-400" : "text-slate-500"
                                            )} />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-white truncate">
                                                    {folder.name}
                                                </span>
                                                {count > 0 && (
                                                    <Badge
                                                        variant="secondary"
                                                        className="bg-slate-700 text-slate-300 text-xs px-2"
                                                    >
                                                        {count}
                                                    </Badge>
                                                )}
                                            </div>
                                            <p className="text-xs text-slate-500 truncate">
                                                {folder.code}
                                            </p>
                                        </div>
                                    </div>

                                    <div
                                        className="flex items-center gap-2 shrink-0"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <div
                                            className="flex items-center gap-2 shrink-0 ml-2"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {isFollowing ? (
                                                <Bell className="w-4 h-4 text-blue-400" />
                                            ) : (
                                                <BellOff className="w-4 h-4 text-slate-600" />
                                            )}
                                            <Switch
                                                checked={isFollowing}
                                                onCheckedChange={() => onToggleFollow(folder.id)}
                                                className={cn(
                                                    "data-[state=checked]:bg-blue-600 data-[state=unchecked]:bg-slate-700 border-2 border-transparent transition-colors",
                                                )}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
